import React, { memo, useMemo } from "react";
import { Modal, Table } from "antd";
import { useSelector } from "react-redux";
import { getAuthToken } from "Helper/AuthTokenHelper";
import { formatDateToDDMMYYYY } from "utils/CommonFunctions";

const operativeProcedureColumns = [
  {
    title: "Sr. No.",
    key: "sno",
    render: (_, __, index) => index + 1,
  },
  {
    title: "Hysteroscopy",
    dataIndex: "hysteroscopy",
    key: "hysteroscopy",
  },
  {
    title: "Hysteroscopy Other",
    dataIndex: "hysteroscopy_other",
    key: "hysteroscopy_other",
  },
  {
    title: "Findings",
    dataIndex: "findings",
    key: "findings",
  },
  {
    title: "TBPCR",
    dataIndex: "tbpcr_operative_procedure",
    key: "tbpcr_operative_procedure",
  },
  {
    title: "Report Done By",
    dataIndex: "report_done_by_operative_procedure",
    key: "report_done_by_operative_procedure",
  },
  {
    title: "Date",
    dataIndex: "date_operative_procedure",
    key: "date_operative_procedure",
    render: (e) => {
      return e ? formatDateToDDMMYYYY(e) : "";
    },
  },
];

const laparoscopyColumns = [
  {
    title: "Sr. No.",
    key: "sno",
    render: (text, data, index) => index + 1,
  },
  {
    title: "Date",
    dataIndex: "date",
    key: "date",
    render: (e) => {
      return e ? formatDateToDDMMYYYY(e) : "";
    },
  },
  {
    title: "Tubes",
    dataIndex: "tubes",
    key: "tubes",
  },
  {
    title: "Ovaries",
    dataIndex: "ovaries",
    key: "ovaries",
  },
  {
    title: "TBPCR",
    dataIndex: "tbpcr",
    key: "tbpcr",
  },
  {
    title: "TBPCR Result",
    dataIndex: "tbpcr_result",
    key: "tbpcr_result",
  },
  {
    title: "Endometriosis",
    dataIndex: "endometriosis",
    key: "endometriosis",
  },
  {
    title: "Report Done By",
    dataIndex: "laparoscopy_report_done_by",
    key: "laparoscopy_report_done_by",
  },
  {
    title: "Report Done By Other",
    dataIndex: "laparoscopy_report_done_by_other",
    key: "laparoscopy_report_done_by_other",
  },
  {
    title: "Note",
    dataIndex: "note",
    key: "note",
  },
];

const FemaleInfertilityInvestigationModal = ({ isOpen, onClose }) => {
  const userData = getAuthToken();

  const theme = localStorage.getItem("default-theme");

  const { activePatientJourneyModuleDetails } = useSelector(
    ({ dashboard }) => dashboard
  );

  const operativeProcedureTableDetails = useMemo(() => {
    return (
      activePatientJourneyModuleDetails?.operative_procedure_table?.map(
        (item, i) => {
          return {
            ...item,
            isDelete: userData?.other === false ? true : false,
            sr_no: i + 1,
          };
        }
      ) || []
    );
  }, [
    activePatientJourneyModuleDetails?.operative_procedure_table,
    userData?.other,
  ]);

  const laparoscopyTableDetails = useMemo(() => {
    return (
      activePatientJourneyModuleDetails?.laparoscopy?.map((item, i) => {
        return {
          ...item,
          isDelete: userData?.other === false ? true : false,
          sr_no: i + 1,
        };
      }) || []
    );
  }, [activePatientJourneyModuleDetails?.laparoscopy, userData?.other]);

  return (
    <>
      <Modal
        centered
        open={isOpen}
        title="Female Infertility Investigation Detail"
        className={`patient-registration-modal theme-${theme}`}
        maskClosable={false}
        onCancel={() => onClose()}
        width={860}
      >
        <div className="modal-details">
          <div className="modal-content-details">
            <div className="details">
              <div className="details-table">
                <div>Patient ID</div>
                <div>{activePatientJourneyModuleDetails?.patient_id}</div>
                <div>Patient Name</div>
                <div>
                  {activePatientJourneyModuleDetails?.patient_full_name}
                </div>
                <div>Partner Name</div>
                <div>
                  {activePatientJourneyModuleDetails?.partner_full_name}
                </div>
              </div>
            </div>
            <div className="details">
              <div className="table-title">Female Details</div>
              <div className="details-table">
                <div>Blood Group</div>
                <div>
                  {activePatientJourneyModuleDetails?.patient_blood_group}
                </div>
                <div>HIV</div>
                <div>{activePatientJourneyModuleDetails?.hiv}</div>
                <div>HBsAg</div>
                <div>{activePatientJourneyModuleDetails?.hbsag}</div>
                <div>VDRL</div>
                <div>{activePatientJourneyModuleDetails?.vdrl}</div>
                <div>TSH (MicroU/ml)</div>
                <div>{activePatientJourneyModuleDetails?.tsh}</div>
                <div>Prolactin (ng/ml)</div>
                <div>{activePatientJourneyModuleDetails?.prolactin}</div>
                <div>RBS (mg/dl)</div>
                <div>{activePatientJourneyModuleDetails?.rbs}</div>
                <div>FSH (miU/ml)</div>
                <div>{activePatientJourneyModuleDetails?.fsh}</div>
                <div>LH (miU/ml)</div>
                <div>{activePatientJourneyModuleDetails?.lh}</div>
                <div>E2 (miU/ml)</div>
                <div>{activePatientJourneyModuleDetails?.e2}</div>
                <div>AMH (ng/ml)</div>
                <div>{activePatientJourneyModuleDetails?.amh}</div>
                <div>AMA (IU/ml)</div>
                <div>{activePatientJourneyModuleDetails?.ama}</div>
                <div>HCV</div>
                <div>{activePatientJourneyModuleDetails?.hcv}</div>
                <div>ATA (IU/ml)</div>
                <div>{activePatientJourneyModuleDetails?.ata}</div>
                <div>S.Creat (mg%)</div>
                <div>{activePatientJourneyModuleDetails?.s_create}</div>
                <div>Progesterone (ng/ml)</div>
                <div>{activePatientJourneyModuleDetails?.progesterone}</div>
                <div>HB1AC (mg%)</div>
                <div>{activePatientJourneyModuleDetails?.ha1ac}</div>
                <div>NK Cell Count</div>
                <div>{activePatientJourneyModuleDetails?.nk_cell_count}</div>
                <div>ACA</div>
                <div>{activePatientJourneyModuleDetails?.aca}</div>
                <div>LA</div>
                <div>{activePatientJourneyModuleDetails?.la}</div>
                <div>ANA</div>
                <div>{activePatientJourneyModuleDetails?.ana}</div>
                <div>APLA</div>
                <div>{activePatientJourneyModuleDetails?.apla}</div>
                <div>HB Electro Phoresis</div>
                <div>
                  {activePatientJourneyModuleDetails?.hb_electro_phoresis}
                </div>
                <div>Torch</div>
                <div>{activePatientJourneyModuleDetails?.torch}</div>
                <div>Karyotype</div>
                <div>{activePatientJourneyModuleDetails?.karyotype}</div>
                <div>ERA Test</div>
                <div>{activePatientJourneyModuleDetails?.era_test}</div>
              </div>
            </div>

            <div className="details">
              <div className="table-title">TVS</div>
              <div className="details-table">
                <div>Uterus</div>
                <div>{activePatientJourneyModuleDetails?.tvs_uterus}</div>
                <div>Tvs Uterus Other</div>
                <div>{activePatientJourneyModuleDetails?.tvs_uterus_other}</div>
                <div>Right Ovaries</div>
                <div>{activePatientJourneyModuleDetails?.right_ovaries}</div>
                <div>Right Ovaries Other</div>
                <div>
                  {activePatientJourneyModuleDetails?.right_ovaries_other}
                </div>
                <div>Right AFC</div>
                <div>{activePatientJourneyModuleDetails?.right_afc}</div>
                <div>Right Era</div>
                <div>{activePatientJourneyModuleDetails?.right_era}</div>
                <div>Right Era Hours</div>
                <div>{activePatientJourneyModuleDetails?.right_era_hours}</div>
                <div>Left Ovaries</div>
                <div>{activePatientJourneyModuleDetails?.left_ovaries}</div>
                <div>Left Ovaries Other</div>
                <div>
                  {activePatientJourneyModuleDetails?.left_ovaries_other}
                </div>
                <div>Left AFC</div>
                <div>{activePatientJourneyModuleDetails?.left_afc}</div>
                <div>Left Era</div>
                <div>{activePatientJourneyModuleDetails?.left_era}</div>
                <div>Left Era Hours</div>
                <div>{activePatientJourneyModuleDetails?.left_era_hours}</div>
              </div>
            </div>
            <div className="details">
              <div className="table-title">HSG</div>
              <div className="details-table">
                <div>Patency of Fallopian Tube (Right)</div>
                <div>
                  {
                    activePatientJourneyModuleDetails?.patency_of_the_fallopian_tube_right
                  }
                </div>
                <div>Patency of Fallopian Tube (Left)</div>
                <div>
                  {
                    activePatientJourneyModuleDetails?.patency_of_the_fallopian_tube_left
                  }
                </div>
                <div>Fallopian Tube (Right)</div>
                <div>
                  {activePatientJourneyModuleDetails?.fallopian_tube_right}
                </div>
                <div>Fallopian Tube (Left)</div>
                <div>
                  {activePatientJourneyModuleDetails?.fallopian_tube_left}
                </div>
                <div>Uterus</div>
                <div>{activePatientJourneyModuleDetails?.hsg_uterus}</div>
                <div>Date</div>
                <div>
                  {activePatientJourneyModuleDetails?.date_hsg
                    ? formatDateToDDMMYYYY(
                        activePatientJourneyModuleDetails?.date_hsg
                      )
                    : ""}
                </div>
              </div>
            </div>
            <div className="details">
              <div className="table-title">Notes</div>
              <div className="details-table">
                <div>Notes</div>
                <div>{activePatientJourneyModuleDetails?.notes}</div>
              </div>
            </div>
          </div>
          <div className="cmn_table_wrap table_wrapper mb-2">
            <div className="table-title">Operative Procedure</div>

            <Table
              columns={operativeProcedureColumns}
              dataSource={operativeProcedureTableDetails}
              pagination={false}
            />
          </div>

          <div className="cmn_table_wrap table_wrapper mb-2">
            <div className="table-title">Laparoscopy</div>

            <Table
              columns={laparoscopyColumns}
              dataSource={laparoscopyTableDetails}
              pagination={false}
            />
          </div>
        </div>
      </Modal>
    </>
  );
};

export default memo(FemaleInfertilityInvestigationModal);
